import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Spin } from "antd";
import {
    CopyOutlined,
    LinkOutlined,
    LoadingOutlined,
    EditOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import Avatar from "@app/components/user/avatar";
import format from "@app/lib/format";
import md from "@app/lib/md";
import logo from "@app/assets/logo-icon2.svg";

import "./style/ai-chat.scoped.scss";

const SpinIcon = <LoadingOutlined style={{ fontSize: 18, marginRight: 10 }} spin />;

const AIChatHistory = observer(({ history: messageHistory = [], onEditMessage, onCancel }) => {
    const bottomRef = useRef(null);
    const history = useHistory();
    const location = useLocation();
    const last = messageHistory[messageHistory.length - 1];
    const allSources = useMemo(() => {
        const sources = [];
        messageHistory.forEach((message) => {
            if (message.answer?.sources) {
                sources.push(...message.answer.sources);
            }
        });

        return sources;
    }, [messageHistory]);

    useEffect(() => {
        if (bottomRef && bottomRef.current) {
            bottomRef.current.scrollIntoView({
                behavior: "smooth",
            });
        }
    }, [messageHistory.length, last?.status]);

    /*
     * Open the file and highlight the source
     */
    function highlightSource(source) {
        const points = source.coordinates.points;
        const width = source.coordinates.layout_width;
        const height = source.coordinates.layout_height;

        const x1 = (points[0][0] / width) * 100; // x from top left corner
        const x2 = (points[2][0] / width) * 100; // x from bottom right corner
        const y1 = (points[0][1] / height) * 100; // y from top left corner
        const y2 = (points[1][1] / height) * 100; // y from bottom left corner

        let area = {
            pageIndex: source.page - 1,
            left: x1,
            top: y1,
            height: y2 - y1,
            width: x2 - x1,
        };

        let link = location.pathname + `?view-file=true&focus=true&area=${JSON.stringify(area)}`;
        history.push(link);
    }

    return (
        <div className="historyContainer">
            {messageHistory.map((item, index) => {
                const isLastMessage = index === messageHistory.length - 1;
                return (
                    <div key={item._id} className="historyGroup">
                        <ChatItem
                            item={item}
                            allSources={allSources}
                            highlightSource={highlightSource}
                            onCancel={isLastMessage ? onCancel : undefined}
                            onEditMessage={onEditMessage}
                        />
                    </div>
                );
            })}
            <div ref={bottomRef}></div>
        </div>
    );
});

const ChatItem = observer(({ item, allSources, highlightSource, onEditMessage, onCancel }) => {
    const contentRef = useRef(null);
    const answer = useMemo(() => {
        const content = item.answer?.content;
        if (!content) {
            return undefined;
        }

        let html = md.toHtml(content);

        const chunkIdToIndex = new Map();
        (allSources || []).forEach((source, idx) => {
            if (source?.chunkId && !chunkIdToIndex.has(source.chunkId)) {
                chunkIdToIndex.set(source.chunkId, idx);
            }
        });

        let counter = 0;
        html = html.replace(/(\s?)\[:ref:([^\]]+):\]/g, (_match, ws, chunkId) => {
            const index = chunkIdToIndex.get(chunkId);
            if (index === undefined) {
                return "";
            }
            const source = allSources[index];
            const fileName = (source?.fileName || "Source").replace(/"/g, "&quot;");
            counter += 1;
            return `${ws}<a href="#" class="source-ref" data-source-index="${index}" title="${fileName}">${counter}</a>`;
        });

        return html;
    }, [item.answer?.content, allSources]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) {
            return;
        }

        const handleClick = (e) => {
            const target = e.target;
            if (target?.classList?.contains("source-ref")) {
                e.preventDefault();
                const idx = parseInt(target.getAttribute("data-source-index"), 10);
                if (!isNaN(idx) && allSources && allSources[idx]) {
                    highlightSource(allSources[idx]);
                }
            }
        };

        el.addEventListener("click", handleClick);
        return () => {
            el.removeEventListener("click", handleClick);
        };
    }, [allSources, highlightSource, answer]);

    const copy = () => {
        const data = {};
        data["text/plain"] = new Blob([item.answer.content], { type: "text/plain" });
        data["text/html"] = new Blob([answer], { type: "text/html" });
        navigator.clipboard.write([new window.ClipboardItem(data)]);
    };

    const handleEdit = () => {
        if (onEditMessage && item.content) {
            onEditMessage(item.content);
        }
    };

    return (
        <div className="chatItem">
            <div className="chatSignature">
                <Avatar className={`avatar`} user={item.user} size={40} />
                <div>
                    <div>{item.user?.fullName}</div>
                    <div className="dateTime">{format.datetime(item.created)}</div>
                </div>
                {onEditMessage && (
                    <div style={{ marginLeft: "auto" }}>
                        <Button
                            type="icon"
                            icon={<EditOutlined />}
                            onClick={handleEdit}
                            title="Edit prompt"
                        />
                    </div>
                )}
            </div>
            <div className="chatContent">{item.content}</div>

            <div className="answer">
                <div className="chatSignature">
                    <img src={logo} className="logo" />
                    <div>
                        <div>CAPTIS Copilot</div>
                        <div className="dateTime">{format.datetime(item.answer.created)}</div>
                    </div>

                    {(item.processing || item.indexing) && (
                        <div className="loading">
                            <Spin indicator={SpinIcon} />
                        </div>
                    )}

                    {item.success && (
                        <div>
                            <Button
                                type="icon"
                                icon={<CopyOutlined />}
                                className="copy"
                                onClick={copy}
                            />
                        </div>
                    )}
                </div>
                {item.indexing && (
                    <div className="chatContent processing-content">
                        <span>
                            Indexing source file.
                            <br />
                            Sit tight, the process may take a few minutes
                        </span>
                        {onCancel && (
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={onCancel}
                                className="cancel-btn"
                                title="Cancel request"
                            />
                        )}
                    </div>
                )}
                {item.processing && (
                    <div className="chatContent processing-content">
                        <span>Processing your request</span>
                        {onCancel && (
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={onCancel}
                                className="cancel-btn"
                                title="Cancel request"
                            />
                        )}
                    </div>
                )}
                {item.failed && <div className="chatContent">Error processing your request</div>}
                {item.success && (
                    <>
                        <div
                            ref={contentRef}
                            className="chatContent html"
                            dangerouslySetInnerHTML={{ __html: answer }}
                        ></div>
                        {item.answer.sources && item.answer.sources.length > 0 && (
                            <div className="sources-list">
                                {item.answer.sources.map(
                                    (source, index) =>
                                        source?.page && (
                                            <div
                                                key={index}
                                                className="link"
                                                onClick={() => highlightSource(source)}
                                            >
                                                <LinkOutlined /> Source{" "}
                                                {item.answer.sources.length > 1
                                                    ? `${index + 1}`
                                                    : ""}
                                            </div>
                                        ),
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

export default AIChatHistory;
